import { QuotaFeatures } from "@/constants";
import { FolderIcon, PencilIcon } from "@phosphor-icons/react";
import {
  useCustomMutation,
  useInvalidate,
  useTranslate,
} from "@refinedev/core";
import { Card, Col, Flex, Grid, Modal, Row, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import { useAntTheme } from "hooks/useAntTheme";
import { useQuota } from "hooks/useQuota";
import React, { useState } from "react";
import { styled } from "styled-components";
import { addDateTz } from "../../utils";
import { secondsToMinutes } from "../helpers/time/secondsToMinutes";
import { ProjectPublish } from "./ProjectPublish";
import { ProjectTagList } from "./ProjectTagList";
import { PublishButton } from "./PublishButton";
import { IProjectDetails } from "./types";
import { radius } from "@libertify/ui";

const { Text } = Typography;

interface ProjectDetailsCardProps {
  disablePublish: boolean;
  projectDetails: IProjectDetails;
}

export const ProjectDetailsCard: React.FC<ProjectDetailsCardProps> = ({
  disablePublish,
  projectDetails,
}) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const breakpoint = Grid.useBreakpoint();
  const [willPublish, setWillPublish] = useState(false);
  const { isBlocked } = useQuota();
  const { mutateAsync } = useCustomMutation();
  const invalidate = useInvalidate();

  const updateProjectTitle = async (title: string) => {
    await mutateAsync({
      url: `media/projects/${projectDetails.projectId}`,
      method: "patch",
      values: {
        ...projectDetails,
        title,
      },
    });
    await invalidate({
      resource: "media/projects",
      id: projectDetails.projectId,
      invalidates: ["detail"],
    });
  };

  const isPublicationQuotaReached = isBlocked(QuotaFeatures.ProjectPublication);

  const disablePublishButton =
    disablePublish || (!projectDetails.isLive && isPublicationQuotaReached);

  return (
    <Card
      style={{
        borderRadius: radius.box,
        border: theme.cardBorder,
        backgroundColor: theme.colorCardRedesign,
        boxShadow: theme.cardBoxShadow,
        width: "100%",
      }}
    >
      {/* Header */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16, marginLeft: 0 }}>
        <Col xs={24} md={22} lg={20} xl={20}>
          <Row gutter={16} style={{ gap: 10 }}>
            <Col
              xs={20}
              style={{
                backgroundColor: theme.colorPrimary,
                borderRadius: "12px",
                maxWidth: "46px",
                height: "46px",
                display: "flex",
                alignItems: "center",
                margin: "auto 0",
              }}
            >
              <FolderIcon
                style={{ color: theme.colorWhite, fontSize: "30px" }}
              />
            </Col>

            <Col xs={20}>
              <StyledEditableText>
                <Typography.Text
                  style={{ width: "100%" }}
                  editable={{
                    triggerType: ["text", "icon"],
                    icon: (
                      <PencilIcon
                        size={24}
                        style={{
                          color: theme.colorPrimary,
                          position: "relative",
                          top: 4,
                        }}
                      />
                    ),
                    onChange: (value: string) => {
                      if (value && value !== projectDetails.project.title) {
                        updateProjectTitle(value);
                      }
                    },
                  }}
                >
                  {projectDetails.project.title}
                </Typography.Text>
              </StyledEditableText>

              <Flex
                align="center"
                wrap="wrap"
                gap={4}
                style={{
                  position: "relative",
                  top: "-5px",
                }}
              >
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {t("media.components.ProjectVideo.createdAt", {
                    timeAgo: dayjs
                      .duration(
                        -dayjs().diff(
                          dayjs(addDateTz(projectDetails.project.created))
                        )
                      )
                      .humanize(true),
                  })}
                </Text>
                {disablePublish ? null : (
                  <>
                    <Typography.Text
                      type="secondary"
                      style={{
                        fontSize: "12px",
                      }}
                    >
                      |{" "}
                      {t("media.components.ProjectVideo.updatedAt", {
                        time: dayjs(
                          addDateTz(projectDetails.mediaCreated)
                        ).format("LLL"),
                      })}
                    </Typography.Text>
                    <Typography.Text
                      type="secondary"
                      style={{
                        fontSize: "12px",
                      }}
                    >
                      |{" "}
                      {t("media.components.ProjectVideo.duration", {
                        duration: secondsToMinutes(
                          projectDetails.project.settings.duration
                        ),
                      })}
                    </Typography.Text>
                  </>
                )}
              </Flex>
            </Col>
          </Row>
        </Col>

        <Col xs={24} md={2} lg={4} xl={4} style={{ margin: "auto 0" }}>
          <Row justify={breakpoint.md ? "end" : "start"}>
            <Tooltip
              title={t("media.components.ProjectVideo.publicationQuotaReached")}
              open={
                !projectDetails.isLive && isPublicationQuotaReached
                  ? undefined
                  : false
              }
            >
              <PublishButton
                projectId={projectDetails.projectId}
                instanceId={projectDetails.instanceId}
                setWillPublish={setWillPublish}
                disabled={disablePublishButton}
              />
            </Tooltip>
          </Row>
        </Col>
      </Row>

      {/* Tags */}
      <Flex justify="space-between" align="center">
        <ProjectTagList
          tags={projectDetails.project.tags}
          projectId={projectDetails.projectId}
        />
      </Flex>

      <Modal
        open={willPublish}
        onCancel={() => setWillPublish(false)}
        style={{ minWidth: "50vw" }}
        footer={null}
        destroyOnClose
      >
        <Row>
          <ProjectPublish
            projectDetails={{
              projectId: projectDetails.projectId,
              instanceId: projectDetails.instanceId,
              organizationId: projectDetails.organizationId,
            }}
            onCancel={() => setWillPublish(false)}
            isLive={projectDetails.isLive}
            publicationUrl={projectDetails.publishedPublicationUrl}
          />
        </Row>
      </Modal>
    </Card>
  );
};

const StyledEditableText = styled.div`
  * {
    font-size: 26px;
    font-weight: 500;
    border: none !important;
    outline: none !important;
    border-radius: 0;
  }
  textarea {
    border-bottom: 3px solid !important;
    margin-left: 12px !important;
    padding: 0;
  }
`;
